import { useState, useContext } from "react";
import { useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  secureDelete, secureGetPdf, securePost
} from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import DeletePopup from "../../components/DeletePopup";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import LoadingLogo from "../../components/LoadingLogo";
import UserContext from "../../Shared/UserContext";

import trashIcon from "../../icons/trashW.svg";
import DownloadIcon from "../../icons/export.svg";
import EditIcon from "../../icons/editB.svg";

const TREATED_COLORS = {
  true: "#06D6A0",
  false: "#ADAFC6",
};

const AvoirFinancierDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { state: { role } } = useContext(UserContext);

  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const afinancier = location.state?.afinancier || {};

  const handleDeleteClick = () => setDeletePopupVisible(true);
  const getTreatedLabel = (status) => t(`treated_status.${status}`);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await secureDelete(`/avoirsFinanciers/delete/${afinancier.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/avoirs-financiers",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.afinancierDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });   
    } catch (error) {
      console.error("Error deleting afinancier:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await secureGetPdf(`/avoirsFinanciers/af/${afinancier.id}`, showSnackbar);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `avoirFinancier/${afinancier.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');
    } catch (error) {
      console.error("Failed to download afinancier:", error);
    }
  };

  const handleEdit = () => {
    history.push({
      pathname: `/avoirs-financiers/modify/${afinancier.id}`,
      state: { initialValues: afinancier, filters: location.state?.filters || {}, page: location.state?.page || 0, size: location.state?.size || 30 },
    });
  };

  const handleTreatClick = async () => {
    const payload = {
      avoirFinancierId: afinancier.id,
      treated: true,
    };


    try {
      await securePost(`/avoirsFinanciers/updateTreated`, payload, showSnackbar);
      showSnackbar(t(`confirmation.treatSuccessAF`), 'success');
    } catch (error) {
      console.error("Error treating the delivery note:", error);
    }
  };

  const renderActionButtons = () => (
    <>
      {/* {!afinancier.treated && (
        <button
          className="flex items-center justify-center bg-[#06D6A0] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2"
          onClick={() => handleTreatClick()}
        >
          <img src={checkIcon} alt="treat" className="w-5 h-5" />
          {t(`actions.treat`)}
        </button>
      )} */}
      <button
        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleEdit}
      >
        <img src={EditIcon} alt="delete" className="w-5 h-5" />
        {t('actions.modify')}
      </button>
      <button
        className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
        onClick={handleDownload}
      >
        <img src={DownloadIcon} alt="delete" className="w-5 h-5" />
        {t('actions.downloadBA')}
      </button>
      {role !== 'ROLE_AGENT' && (
        <button
          className="flex items-center justify-center bg-[#EF476F] text-white px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2"
          onClick={handleDeleteClick}
        >
          <img src={trashIcon} alt="delete" className="w-5 h-5" />
          {t('actions.delete')}
        </button>
      )}
    </>
  );

  if (loading) return <LoadingLogo />;


  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.afinancier_details")}</title>
      </Helmet>

      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-black">
          {t('titles.afinancier_details')}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#6A78F8] text-[20px] font-semibold">
              {afinancier.client?.code} - {afinancier.client?.name}
            </p>
            <span
              className="text-white text-[12px] font-semibold px-2 py-1 rounded-full text-center"
              style={{
                backgroundColor: TREATED_COLORS[afinancier.treated] || "#A8ADC3",
              }}
            >
              {getTreatedLabel(afinancier.treated).toUpperCase()}
            </span>
          </div>
          <div className="flex space-x-4">{renderActionButtons()}</div>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.reference')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {afinancier.avoirFinancierNumber}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.creationDate')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(afinancier.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('columns.by')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {afinancier.user?.firstName} {afinancier.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t('rows.commercial')}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {afinancier.commercial?.firstName} {afinancier.commercial?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(afinancier.priceTTC)}
          </p>
        </div>
      </div>

      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t('fields_types.list')}
      </h3>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {afinancier.details?.map((item, index) => (
          <div
            key={index}
            className="bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
          >
            <p className="text-[#38485C] text-[18px] font-semibold">
              {item.designation}
            </p>
            <p className="text-[#043F88] text-[14px] font-bold">
              {formatAmount(item.priceTTC)}
            </p>
          </div>
        ))}
      </div>


      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
    </div>
  );
};

export default AvoirFinancierDetails;
